import axios from 'axios';
import localforage from 'localforage';
import { Store, PurchaseOrder, PurchaseItem, ReviewTask } from '../types';

// API 基礎 URL
const API_BASE_URL = 'https://shop0.holy-dream-4aa9.workers.dev/api/v1';

// 創建 axios 實例
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 離線模式標誌
let isOfflineMode = false;

// 檢查網絡連接
const checkConnection = async () => {
  try {
    await axios.get(`${API_BASE_URL}/health`);
    isOfflineMode = false;
    return true;
  } catch (error) {
    isOfflineMode = true;
    return false;
  }
};

// 初始化 localforage
localforage.config({
  name: 'ecommerce-system',
  storeName: 'offline-data',
});

// 通用 API 請求函數，支持離線模式
const request = async (method: string, url: string, data?: any) => {
  // 檢查網絡連接
  const isOnline = await checkConnection();

  // 生成緩存鍵
  const cacheKey = `${method}:${url}:${data ? JSON.stringify(data) : ''}`;

  if (isOnline) {
    try {
      let response;

      switch (method.toUpperCase()) {
        case 'GET':
          response = await axiosInstance.get(url);
          break;
        case 'POST':
          response = await axiosInstance.post(url, data);
          break;
        case 'PUT':
          response = await axiosInstance.put(url, data);
          break;
        case 'DELETE':
          response = await axiosInstance.delete(url);
          break;
        default:
          throw new Error(`不支持的方法: ${method}`);
      }

      // 緩存響應數據
      await localforage.setItem(cacheKey, {
        data: response.data,
        timestamp: Date.now(),
      });

      return response.data;
    } catch (error) {
      // 如果在線請求失敗，嘗試從緩存獲取數據
      const cachedData = await localforage.getItem(cacheKey);
      if (cachedData && typeof cachedData === 'object' && 'data' in cachedData) {
        return cachedData.data;
      }
      throw error;
    }
  } else {
    // 離線模式：從緩存獲取數據
    const cachedData = await localforage.getItem(cacheKey);
    if (cachedData && typeof cachedData === 'object' && 'data' in cachedData) {
      return cachedData.data;
    }

    // 如果是 POST/PUT/DELETE 請求，將其保存到離線隊列
    if (method.toUpperCase() !== 'GET') {
      const offlineQueue = await localforage.getItem('offline-queue') || [];
      const queueArray = Array.isArray(offlineQueue) ? offlineQueue : [];
      await localforage.setItem('offline-queue', [
        ...queueArray,
        { method, url, data, timestamp: Date.now() },
      ]);
    }

    throw new Error('離線模式：無法獲取數據');
  }
};

// 同步離線隊列
export const syncOfflineQueue = async () => {
  const isOnline = await checkConnection();
  if (!isOnline) return;

  const offlineQueue = await localforage.getItem('offline-queue') || [];
  if (!Array.isArray(offlineQueue) || offlineQueue.length === 0) return;

  const newQueue = [];

  for (const item of offlineQueue) {
    try {
      await request(item.method, item.url, item.data);
    } catch (error) {
      newQueue.push(item);
    }
  }

  await localforage.setItem('offline-queue', newQueue);
};

// API 函數
export const api = {
  // 店鋪相關
  stores: {
    getAll: () => request('GET', '/stores') as Promise<Store[]>,
    getById: (id: string) => request('GET', `/stores/${id}`) as Promise<Store>,
    create: (data: Partial<Store>) => request('POST', '/stores', data) as Promise<Store>,
    update: (id: string, data: Partial<Store>) => request('PUT', `/stores/${id}`, data) as Promise<Store>,
    delete: (id: string) => request('DELETE', `/stores/${id}`) as Promise<void>,
    getPurchaseOrders: (id: string) => request('GET', `/stores/${id}/purchase-orders`) as Promise<PurchaseOrder[]>,
    getReviewTasks: (id: string) => request('GET', `/stores/${id}/review-tasks`) as Promise<ReviewTask[]>,
  },

  // 採購訂單相關
  purchaseOrders: {
    getAll: () => request('GET', '/purchase-orders') as Promise<PurchaseOrder[]>,
    getById: (id: string) => request('GET', `/purchase-orders/${id}`) as Promise<PurchaseOrder>,
    create: (data: Partial<PurchaseOrder>) => request('POST', '/purchase-orders', data) as Promise<PurchaseOrder>,
    update: (id: string, data: Partial<PurchaseOrder>) => request('PUT', `/purchase-orders/${id}`, data) as Promise<PurchaseOrder>,
    delete: (id: string) => request('DELETE', `/purchase-orders/${id}`) as Promise<void>,
    getItems: (id: string) => request('GET', `/purchase-orders/${id}/items`) as Promise<PurchaseItem[]>,
    updateCourier: (id: string, data: { courier_number: string; courier_status: string }) =>
      request('PUT', `/purchase-orders/${id}/courier`, data) as Promise<PurchaseOrder>,
    // 添加這些方法以支持 PurchaseOrderForm.tsx 中的調用
    createItem: (data: Partial<PurchaseItem>) => request('POST', '/purchase-items', data) as Promise<PurchaseItem>,
    updateItem: (id: string, data: Partial<PurchaseItem>) => request('PUT', `/purchase-items/${id}`, data) as Promise<PurchaseItem>,
  },

  // 測評任務相關
  reviewTasks: {
    getAll: () => request('GET', '/review-tasks') as Promise<ReviewTask[]>,
    getById: (id: string) => request('GET', `/review-tasks/${id}`) as Promise<ReviewTask>,
    create: (data: Partial<ReviewTask>) => request('POST', '/review-tasks', data) as Promise<ReviewTask>,
    update: (id: string, data: Partial<ReviewTask>) => request('PUT', `/review-tasks/${id}`, data) as Promise<ReviewTask>,
    delete: (id: string) => request('DELETE', `/review-tasks/${id}`) as Promise<void>,
    updateStatus: (id: string, data: Partial<ReviewTask>) => request('PUT', `/review-tasks/${id}/status`, data) as Promise<ReviewTask>,
  },
};

export default api;
